import prisma from './database';

// Park queries
export async function getParksWithFilters(filters = {}, pagination = {}) {
  const { page = 1, limit = 50 } = pagination;
  const {
    state,
    designation,
    search,
    sortBy = 'name',
    sortOrder = 'asc',
  } = filters;

  const skip = (page - 1) * limit;

  const where = {};
  if (state) where.states = { contains: state };
  if (designation) where.designation = designation;
  if (search) {
    where.OR = [
      { name: { contains: search, mode: 'insensitive' } },
      { fullName: { contains: search, mode: 'insensitive' } },
      { description: { contains: search, mode: 'insensitive' } },
    ];
  }

  const orderBy = { [sortBy]: sortOrder };

  const [parks, total] = await Promise.all([
    prisma.park.findMany({
      where,
      orderBy,
      skip,
      take: limit,
      select: {
        id: true,
        parkCode: true,
        fullName: true,
        name: true,
        description: true,
        designation: true,
        url: true,
        latitude: true,
        longitude: true,
        states: true,
        slug: true,
        images: true,
        _count: {
          select: {
            ParkEvent: true,
            ParkActivity: true,
            ParkTopic: true,
          },
        },
      },
    }),
    prisma.park.count({ where }),
  ]);

  return {
    data: parks,
    pagination: {
      page,
      limit,
      total,
      totalPages: Math.ceil(total / limit),
      hasNext: page < Math.ceil(total / limit),
      hasPrev: page > 1,
    },
  };
}

export async function getParkBySlug(slug, includeRelations = true) {
  const include = includeRelations
    ? {
        ParkEvent: {
          include: {
            Event: true,
          },
        },
        ParkActivity: {
          include: {
            Activity: true,
          },
        },
        ParkTopic: {
          include: {
            Topic: true,
          },
        },
      }
    : {};

  return await prisma.park.findUnique({
    where: { slug },
    include,
  });
}

export async function getParkByCode(parkCode) {
  return await prisma.park.findUnique({
    where: { parkCode },
  });
}

// Event queries
export async function getEventsWithFilters(filters = {}, pagination = {}) {
  const { page = 1, limit = 20 } = pagination;
  const {
    category,
    dateStart,
    dateEnd,
    search,
    sortBy = 'dateStart',
    sortOrder = 'asc',
  } = filters;

  const skip = (page - 1) * limit;

  const where = {};
  if (category) where.category = category;
  if (dateStart) where.dateStart = { gte: dateStart };
  if (dateEnd) where.dateEnd = { lte: dateEnd };
  if (search) {
    where.OR = [
      { title: { contains: search, mode: 'insensitive' } },
      { description: { contains: search, mode: 'insensitive' } },
      { location: { contains: search, mode: 'insensitive' } },
    ];
  }

  const orderBy = { [sortBy]: sortOrder };

  const [events, total] = await Promise.all([
    prisma.event.findMany({
      where,
      orderBy,
      skip,
      take: limit,
      select: {
        id: true,
        eventId: true,
        title: true,
        description: true,
        category: true,
        dateStart: true,
        dateEnd: true,
        times: true,
        timeInfo: true,
        location: true,
        images: true,
        slug: true,
        parkFullName: true,
        parkCode: true,
        types: true,
        isFree: true,
      },
    }),
    prisma.event.count({ where }),
  ]);

  return {
    data: events,
    pagination: {
      page,
      limit,
      total,
      totalPages: Math.ceil(total / limit),
      hasNext: page < Math.ceil(total / limit),
      hasPrev: page > 1,
    },
  };
}

export async function getEventBySlug(slug) {
  // First try exact slug match
  let event = await prisma.event.findUnique({
    where: { slug },
  });

  // If not found, try to find by matching slugified title
  // This handles events that don't have slug field populated
  if (!event) {
    const { slugify } = require('./util');
    // Convert slug back to approximate search term (replace hyphens with spaces)
    const searchTerm = slug.replace(/-/g, ' ');
    const candidates = await prisma.event.findMany({
      where: {
        title: { contains: searchTerm, mode: 'insensitive' },
      },
      take: 50,
      select: {
        id: true,
        eventId: true,
        title: true,
        description: true,
        category: true,
        types: true,
        dateStart: true,
        dateEnd: true,
        dates: true,
        times: true,
        timeInfo: true,
        isRecurring: true,
        recurrenceDateStart: true,
        recurrenceDateEnd: true,
        isFree: true,
        isRegresRequired: true,
        regresInfo: true,
        regresUrl: true,
        infoUrl: true,
        location: true,
        latitude: true,
        longitude: true,
        contactName: true,
        contactTelephone: true,
        contactEmail: true,
        images: true,
        slug: true,
        parkFullName: true,
        parkCode: true,
      },
    });

    event = candidates.find((e) => slugify(e.title) === slug);
  }

  return event;
}

// Search queries
export async function searchParksAndEvents(query, limit = 10) {
  const [parks, events] = await Promise.all([
    prisma.park.findMany({
      where: {
        OR: [
          { name: { contains: query, mode: 'insensitive' } },
          { fullName: { contains: query, mode: 'insensitive' } },
          { description: { contains: query, mode: 'insensitive' } },
        ],
      },
      take: limit,
      select: {
        id: true,
        name: true,
        fullName: true,
        slug: true,
        designation: true,
        states: true,
        images: true,
      },
    }),
    prisma.event.findMany({
      where: {
        OR: [
          { title: { contains: query, mode: 'insensitive' } },
          { description: { contains: query, mode: 'insensitive' } },
          { location: { contains: query, mode: 'insensitive' } },
        ],
      },
      take: limit,
      select: {
        id: true,
        title: true,
        description: true,
        category: true,
        dateStart: true,
        location: true,
        slug: true,
        images: true,
      },
    }),
  ]);

  return { parks, events };
}

// Statistics queries
export async function getParkStatistics() {
  const [totalParks, totalEvents, totalActivities, totalTopics] =
    await Promise.all([
      prisma.park.count(),
      prisma.event.count(),
      prisma.activity.count(),
      prisma.topic.count(),
    ]);

  return {
    totalParks,
    totalEvents,
    totalActivities,
    totalTopics,
    lastUpdated: new Date().toISOString(),
  };
}

// Get all parks (for homepage)
export async function getAllParks() {
  return await prisma.park.findMany({
    orderBy: { fullName: 'asc' },
    select: {
      id: true,
      parkCode: true,
      fullName: true,
      name: true,
      designation: true,
      states: true,
      slug: true,
      images: true,
      addresses: true,
      ParkActivity: {
        select: {
          Activity: {
            select: {
              id: true,
              name: true,
            },
          },
        },
      },
    },
  });
}

// Get parks with minimal data for find-a-park page (lighter payload)
export async function getParksForListing() {
  const parks = await prisma.park.findMany({
    orderBy: { fullName: 'asc' },
    select: {
      id: true,
      fullName: true,
      name: true,
      description: true,
      designation: true,
      latitude: true,
      longitude: true,
      states: true,
      slug: true,
      images: true,
      ParkActivity: {
        select: {
          Activity: {
            select: {
              name: true,
            },
          },
        },
      },
      ParkAmenity: {
        select: {
          Amenity: {
            select: {
              name: true,
            },
          },
        },
      },
    },
  });

  // Optimize payload: flatten nested objects, trim images, truncate descriptions
  return parks.map((park) => ({
    id: park.id,
    fullName: park.fullName,
    name: park.name,
    description: park.description?.slice(0, 200) || '',
    designation: park.designation,
    latitude: park.latitude,
    longitude: park.longitude,
    states: park.states,
    slug: park.slug,
    images: park.images?.slice(0, 1) || [],
    // Flatten to arrays of names (much smaller than nested objects)
    activities:
      park.ParkActivity?.map((pa) => pa.Activity?.name).filter(Boolean) || [],
    amenities:
      park.ParkAmenity?.map((pa) => pa.Amenity?.name).filter(Boolean) || [],
  }));
}

// Get all events (for getStaticPaths and homepage)
export async function getAllEvents() {
  return await prisma.event.findMany({
    orderBy: { dateStart: 'asc' },
    select: {
      id: true,
      eventId: true,
      title: true,
      description: true,
      category: true,
      types: true,
      dateStart: true,
      dateEnd: true,
      dates: true,
      times: true,
      timeInfo: true,
      isRecurring: true,
      recurrenceDateStart: true,
      recurrenceDateEnd: true,
      isFree: true,
      isRegresRequired: true,
      regresInfo: true,
      regresUrl: true,
      infoUrl: true,
      location: true,
      latitude: true,
      longitude: true,
      contactName: true,
      contactTelephone: true,
      contactEmail: true,
      images: true,
      slug: true,
      parkFullName: true,
      parkCode: true,
    },
  });
}

// Get events by park code (for park detail page)
export async function getEventsByParkCode(parkCode) {
  return await prisma.event.findMany({
    where: { parkCode },
    orderBy: { dateStart: 'asc' },
    select: {
      id: true,
      eventId: true,
      title: true,
      description: true,
      category: true,
      types: true,
      dateStart: true,
      dateEnd: true,
      dates: true,
      times: true,
      timeInfo: true,
      isRecurring: true,
      recurrenceDateStart: true,
      recurrenceDateEnd: true,
      isFree: true,
      isRegresRequired: true,
      regresInfo: true,
      regresUrl: true,
      infoUrl: true,
      location: true,
      latitude: true,
      longitude: true,
      contactName: true,
      contactTelephone: true,
      contactEmail: true,
      images: true,
      slug: true,
      parkFullName: true,
      parkCode: true,
    },
  });
}

// Get park by fullName
export async function getParkByFullName(fullName) {
  return await prisma.park.findUnique({
    where: { fullName },
    select: { fullName: true, images: true, parkCode: true, slug: true },
  });
}

// Get park name options for select dropdown
export async function getParkNameOptions() {
  const parks = await prisma.park.findMany({
    select: {
      fullName: true,
      slug: true,
    },
    orderBy: { fullName: 'asc' },
  });
  return parks.map((park) => ({
    value: park.slug,
    label: park.fullName,
  }));
}

// Get park codes by state abbreviation
export async function getParkCodesByState(stateAbbr) {
  const parks = await prisma.park.findMany({
    where: {
      states: { contains: stateAbbr },
    },
    select: { parkCode: true },
  });
  return parks.map((p) => p.parkCode);
}

// Get park coordinates (lightweight query for event coordinate lookups)
export async function getParkCoordinates() {
  return prisma.park.findMany({
    select: { parkCode: true, latitude: true, longitude: true },
  });
}

// Get all states and park codes mapping
export async function getStatesAndParkCodes() {
  const parks = await prisma.park.findMany({
    select: {
      parkCode: true,
      states: true,
    },
  });
  return parks.map((park) => ({
    parkCode: park.parkCode,
    state: park.states,
  }));
}
